import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, CircularProgress, Grid, Paper, Chip, IconButton,
} from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import TextField from '@mui/material/TextField';
import { getOrderHisPanel } from "../../services/OrderHisServices";
import { getOrderHisDetailPanel } from "../../services/OrderHisDetailServices";
import CustomDataGrid from "../../components/DataGrid/CustomDataGrid";
import Paginator from "../../components/paginator/Paginator";
import { getHeaders } from "./configGrid/headers";
import { formatRows } from "./configGrid/rows";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import OrderDetailModal from "./orderDetailModal";
import { ObservacionModal } from './orderObservacionesModal';
import { AuthContext } from '../../context/authContext';
import { handleOpenPdf } from '../../components/pdf/exportPdf';
import { formatHeaderPDF } from "./configPDF/headerPDF";
import { columns } from "./configPDF/columnsPDF";
import { formatRowsPDF } from "./configPDF/rowsPDF";
import ComprobantePDF from "../../components/Imprimir/ComprobantePDF";

const datePickerSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-md)',
    fontFamily: 'var(--font-body)',
    fontSize: '0.85rem',
    backgroundColor: 'var(--bg-subtle)',
    '& fieldset': { border: '2px solid transparent' },
    '&:hover fieldset': { border: '2px solid var(--border-hover)' },
    '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
    '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'var(--font-body)', fontSize: '0.85rem',
    '&.Mui-focused': { color: 'var(--accent)' },
  },
};

const OrderHistory = () => {
  const { clienteSelected, empresa } = useContext(AuthContext);
  const [pedidos, setPedidos] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [dateFromFilter, setDateFromFilter] = useState(null);
  const [dateUntilFilter, setDateUntilFilter] = useState(null);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [numPagina, setNumPagina] = useState(1);
  const [filas, setFilas] = useState(10);
  const [detallePedido, setDetallePedido] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalObservacionOpen, setModalObservacionOpen] = useState(false);
  const [observacionSeleccionada, setObservacionSeleccionada] = useState('');
  const [pedidoNro, setPedidoNro] = useState(null);
  const [user, setUser] = useState(null);
  const [headerPDF, setHeaderPDF] = useState(null);
  const [columnsPDF, setColumnsPDF] = useState([]);  // ✅ typo corregido
  const [rowsPDF, setRowsPDF] = useState([]);
  const [pdfReady, setPdfReady] = useState(false);

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    if (u) setUser(u);
  }, []);

  const rolId = user?.rol_id;
  const clienteDelUsuario = user?.id;

  useEffect(() => {
    if (rolId === 1 && clienteSelected) setClienteSeleccionado(clienteSelected.id);
    if (rolId === 2 && clienteDelUsuario) setClienteSeleccionado(clienteDelUsuario);
  }, [user, clienteSelected]);

  useEffect(() => {
    const cargarPedidos = async () => {
      if (!clienteSeleccionado) return;
      setIsLoading(true);
      try {
        const res = await getOrderHisPanel(
          clienteSeleccionado, "",
          dateFromFilter ? dayjs(dateFromFilter).format("YYYY-MM-DD") : "",
          dateUntilFilter ? dayjs(dateUntilFilter).format("YYYY-MM-DD") : "",
          numPagina, filas
        );
        setPedidos(res.pedidos || []);
        setTotalItems(res.pagination.totalItems || 0);
      } catch (err) { console.error("Error al cargar pedidos:", err); }
      finally { setIsLoading(false); }
    };
    cargarPedidos();
  }, [clienteSeleccionado, dateFromFilter, dateUntilFilter, numPagina, filas]);

  const handleOpenDetallePedido = async (pedidoId) => {
    try {
      const res = await getOrderHisDetailPanel(pedidoId, null, 1, 100);
      setDetallePedido(res.orderHisDetails || []);
      setModalOpen(true);
    } catch (error) { console.error("Error al cargar detalles:", error); }
  };

  const handleOpenObservacion = (observacion, pedidoId) => {
    setObservacionSeleccionada(observacion);
    setPedidoNro(pedidoId);
    setModalObservacionOpen(true);
  };


  const handleCreatePDF = async (pedido) => {
    try {
      setHeaderPDF(null);
      setColumnsPDF([]);   // ✅ typo corregido
      setRowsPDF([]);
      setPdfReady(false);

      // ✅ Faltaba el await — sin esto detalle siempre era una Promise, nunca los datos
      const data = await getOrderHisDetailPanel(pedido.id, null, 1, 99);

      console.log("data",data)
      const detalle = data?.orderHisDetails || [];

      setHeaderPDF(formatHeaderPDF(pedido));
      setColumnsPDF(columns);  // ✅ typo corregido
      setRowsPDF(formatRowsPDF(detalle));
      setPdfReady(true);

    } catch (err) {
      console.error("Error al generar PDF:", err);
    } finally {
      // ✅ Si hay error, el pdfReady queda en false correctamente
      // (solo se setea true si todo salió bien, arriba del catch)
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} locale="es">
      <Box sx={{ width: '100%' }}>
        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 'var(--radius-md)', backgroundColor: 'rgba(124,58,237,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <HistoryOutlinedIcon sx={{ color: 'var(--purple)', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.15rem', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
                Historial de Pedidos
              </Typography>
              <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                Tus compras anteriores
              </Typography>
            </Box>
          </Box>
          {totalItems > 0 && (
            <Chip
              label={`${totalItems} pedido${totalItems !== 1 ? 's' : ''}`}
              size="small"
              sx={{
                fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.72rem',
                backgroundColor: 'rgba(124,58,237,0.08)', color: 'var(--purple)',
                border: '1px solid var(--purple)',
              }}
            />
          )}
        </Box>

        {/* ── Filtros de fecha ── */}
        <Paper elevation={0} sx={{
          p: 2, mb: 2, borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)', backgroundColor: 'var(--bg-subtle)',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <CalendarMonthOutlinedIcon sx={{ color: 'var(--text-muted)', fontSize: 20 }} />
          <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: '0.82rem', color: 'var(--text-secondary)', mr: 1 }}>
            Filtrar por fecha:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ position: 'relative' }}>
              <DatePicker
                label="Desde"
                value={dateFromFilter ? dayjs(dateFromFilter) : null}
                onChange={(v) => setDateFromFilter(v ? v.format('YYYY-MM-DD') : null)}
                inputFormat="DD/MM/YYYY"
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ ...datePickerSx, width: 160 }} />
                )}
              />
              {dateFromFilter && (
                <IconButton size="small" onClick={() => setDateFromFilter(null)}
                  sx={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20 }}>
                  <ClearIcon sx={{ fontSize: 14, color: 'var(--text-muted)' }} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ position: 'relative' }}>
              <DatePicker
                label="Hasta"
                value={dateUntilFilter ? dayjs(dateUntilFilter) : null}
                onChange={(v) => setDateUntilFilter(v ? v.format('YYYY-MM-DD') : null)}
                inputFormat="DD/MM/YYYY"
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ ...datePickerSx, width: 160 }} />
                )}
              />
              {dateUntilFilter && (
                <IconButton size="small" onClick={() => setDateUntilFilter(null)}
                  sx={{ position: 'absolute', right: 32, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20 }}>
                  <ClearIcon sx={{ fontSize: 14, color: 'var(--text-muted)' }} />
                </IconButton>
              )}
            </Box>
          </Box>
        </Paper>

        {/* ── Content ── */}
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 1.5 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: 'var(--radius-md)', backgroundColor: 'rgba(124,58,237,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} sx={{ color: 'var(--purple)' }} />
            </Box>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.85rem', color: 'var(--text-muted)' }}>Cargando historial...</Typography>
          </Box>
        ) : pedidos.length === 0 ? (
          <Paper elevation={0} sx={{
            p: 4, textAlign: 'center', borderRadius: 'var(--radius-xl)',
            border: '1px solid var(--border)', backgroundColor: 'var(--bg-subtle)',
          }}>
            <HistoryOutlinedIcon sx={{ fontSize: 48, color: 'var(--text-muted)', opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem', color: 'var(--text-secondary)', mb: 0.5 }}>
              No hay pedidos en el historial
            </Typography>
            <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Tus compras completadas aparecerán acá
            </Typography>
          </Paper>
        ) : (
          <>
            <CustomDataGrid
              rows={formatRows(pedidos)}
              columns={getHeaders(handleOpenObservacion, handleOpenDetallePedido, handleCreatePDF)}
              enableSelectionClick={false}
            />

            <Box sx={{
              mt: 2, p: 1.5,
              backgroundColor: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}>
              <Paginator
                numPagina={numPagina}
                setNumPagina={setNumPagina}
                totalPages={Math.ceil(totalItems / filas)}
                filas={filas}
                setFilas={setFilas}
                totalDataItems={totalItems}
              />
            </Box>

            <OrderDetailModal open={modalOpen} onClose={() => { setModalOpen(false); setDetallePedido([]); }} pedidoDetalles={detallePedido} />
            <ObservacionModal open={modalObservacionOpen} onClose={() => { setModalObservacionOpen(false); setObservacionSeleccionada(''); }} observacion={observacionSeleccionada} pedidoNro={pedidoNro} />
            <ComprobantePDF
              header={headerPDF}
              columns={columnsPDF}
              rows={rowsPDF}
              titlePdf="Pedido"
              empresa={empresa}
              trigger={pdfReady}
              onFinish={() => setPdfReady(false)}
            />
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default OrderHistory;